import React, { useState } from 'react';
import { useDelivery } from '../context/DeliveryContext';
import { useAuth } from '../context/AuthContext';
import api from '../services';

interface DeliveryInstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (instructions: string) => void;
  initialValue?: string;
}

export const DeliveryInstructionsModal: React.FC<DeliveryInstructionsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialValue = '',
}) => {
  const { delivery, setDelivery } = useDelivery();
  const { user } = useAuth();
  const [instructions, setInstructions] = useState(delivery.instructions || initialValue);
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const handleDone = async () => {
    try {
      setSaving(true);

      // Update context
      setDelivery((prev) => ({
        ...prev,
        instructions,
      }));

      // Call PUT API if we have a saved address ID
      const addressId = delivery.locationObject?.id;
      if (addressId && user?.id && !delivery.locationObject?.isSearchedAddress) {
        await api.put(
          `${process.env.REACT_APP_API_URL}/address/${addressId}`,
          { deliveryInstructions: instructions }
        );
        console.log("✅ Delivery instructions updated successfully");
      }

      onSave(instructions);
      onClose();
    } catch (error) {
      console.error("❌ Failed to update delivery instructions:", error);
      // Still save locally even if API fails
      onSave(instructions);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 m-0"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="m-0 text-xl font-semibold text-black">
            Delivery instructions
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 font-bold hover:text-black transition-colors"
            aria-label="Close modal"
          >
           X
          </button>
        </div>

        {/* Textarea */}
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder="Enter any specific delivery instructions"
          maxLength={280}
          className="w-full h-32 px-2 py-2 border-2 border-gray-300 rounded-lg resize-none focus:outline-none focus:border-black transition-colors text-black placeholder-gray-400"
        />

        {/* Done Button */}
        <button
          onClick={handleDone}
          disabled={saving}
          className="w-full mt-4 bg-black toggle-selected-text py-3.5 rounded-lg font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-1 disabled:opacity-50"
        >
          {saving ? "Saving..." : "Done"}
        </button>
      </div>
    </div>
  );
};

export default DeliveryInstructionsModal;
